import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? articles[slug] : null;
  const SITE_URL = __VITE_SITE_URL__;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-32 pb-20 text-center">
          <h1 className="font-heading text-3xl font-bold text-foreground-950 mb-4">Articolo non trovato</h1>
          <p className="text-foreground-500 mb-6">L'articolo che stai cercando non è disponibile.</p>
          <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
            <i className="ri-home-4-line"></i>
            Torna alla Home
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Breadcrumb */}
      <div className="pt-20 bg-background-100">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-4">
          <nav className="flex items-center gap-2 text-xs text-foreground-400" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary-500 transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line text-[10px]"></i>
            <span className="text-foreground-600">Articolo</span>
            <i className="ri-arrow-right-s-line text-[10px]"></i>
            <span className="text-foreground-800 font-medium truncate max-w-[200px]">{article.title}</span>
          </nav>
        </div>
      </div>

      <main>
        {/* Hero */}
        <section className="bg-background-100 pb-12">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="rounded-2xl overflow-hidden mb-10">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-[250px] md:h-[400px] object-cover"
              />
            </div>

            {/* Article header */}
            <div className="max-w-3xl mx-auto">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 bg-accent-100 text-accent-800 rounded-full text-xs font-medium">
                  Guida
                </span>
                <span className="text-xs text-foreground-400">Aggiornato 2026</span>
              </div>
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-6">
                {article.h1}
              </h1>
              <p className="text-foreground-500 text-base leading-relaxed">
                {article.introduction}
              </p>
            </div>
          </div>
        </section>

        {/* Article body */}
        <article className="py-12 md:py-16">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-12">
              {/* Main content */}
              <div className="max-w-none">
                {/* Quick nav */}
                <div className="bg-background-100 rounded-xl p-6 mb-10">
                  <h3 className="text-sm font-semibold text-foreground-800 mb-4">In questo articolo:</h3>
                  <ul className="space-y-2">
                    {article.sections.map((section, i) => (
                      <li key={section.id}>
                        <a
                          href={`#${section.id}`}
                          className="flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-500 transition-colors"
                        >
                          <span className="w-5 h-5 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 text-xs font-medium flex-shrink-0">
                            {i + 1}
                          </span>
                          {section.title}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Sections */}
                {article.sections.map((section) => (
                  <section key={section.id} id={section.id} className="mb-10 scroll-mt-24">
                    <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                      {section.title}
                    </h2>
                    <div className="text-foreground-600 text-base leading-relaxed space-y-4">
                      {section.content.split('\n').filter(Boolean).map((paragraph, i) => (
                        <p key={i}>{paragraph}</p>
                      ))}
                    </div>
                  </section>
                ))}

                {/* FAQ Schema */}
                <section className="mt-14">
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-8">
                    Domande Frequenti
                  </h2>
                  <div className="space-y-4">
                    {article.faqs.map((faq, i) => (
                      <details key={i} className="group bg-background-100 rounded-xl overflow-hidden">
                        <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-foreground-800 hover:text-primary-500 transition-colors">
                          <span className="text-sm pr-4">{faq.question}</span>
                          <i className="ri-arrow-down-s-line group-open:rotate-180 transition-transform text-foreground-400"></i>
                        </summary>
                        <div className="px-6 pb-4 text-sm text-foreground-500 leading-relaxed">
                          {faq.answer}
                        </div>
                      </details>
                    ))}
                  </div>
                </section>

                {/* CTA */}
                <div className="mt-14 bg-primary-500/5 border border-primary-200/50 rounded-2xl p-8 text-center">
                  <h3 className="font-heading text-xl font-bold text-foreground-950 mb-3">
                    Pronto a scoprire Itaobuy Spreadsheet?
                  </h3>
                  <p className="text-foreground-500 text-sm mb-6 max-w-md mx-auto">
                    Visita il nostro sito principale per esplorare tutte le categorie e trovare i migliori prodotti.
                  </p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                    <a
                      href="https://kaspreadsheet.com"
                      target="_blank"
                      rel="noopener nofollow"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      Itaobuy Sheet
                      <i className="ri-arrow-right-up-line"></i>
                    </a>
                    <Link
                      to="/"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-background-200 text-foreground-700 rounded-full text-sm font-medium hover:bg-background-300 transition-colors whitespace-nowrap"
                    >
                      <i className="ri-home-4-line"></i>
                      Torna alla Home
                    </Link>
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <aside className="hidden lg:block">
                <div className="sticky top-24">
                  {/* Related categories */}
                  <div className="bg-background-100 rounded-xl p-5 mb-6">
                    <h4 className="text-xs uppercase tracking-wider text-foreground-400 mb-4 font-label">
                      Categorie
                    </h4>
                    <div className="flex flex-col gap-2">
                      {categories.slice(0, 8).map((cat) => (
                        <a
                          key={cat.id}
                          href={cat.url}
                          target="_blank"
                          rel="noopener nofollow"
                          className="text-sm text-foreground-600 hover:text-primary-500 transition-colors py-1.5 border-b border-background-200/50 last:border-0 whitespace-nowrap"
                        >
                          {cat.name}
                        </a>
                      ))}
                    </div>
                  </div>

                  {/* Internal link back to homepage */}
                  <Link
                    to="/"
                    className="block bg-foreground-950 text-background-50 rounded-xl p-5 hover:bg-foreground-800 transition-colors"
                  >
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-background-50/10 mb-4">
                      <i className="ri-home-4-line text-background-50 text-lg"></i>
                    </div>
                    <h4 className="font-semibold text-sm mb-1">Torna alla Homepage</h4>
                    <p className="text-background-50/60 text-xs leading-relaxed">
                      Esplora tutte le categorie, guide e link aggiornati su Itaobuy Spreadsheet Italia.
                    </p>
                    <div className="flex items-center gap-1.5 mt-3 text-accent-300 text-xs font-medium">
                      <span>Vai alla Home</span>
                      <i className="ri-arrow-right-line"></i>
                    </div>
                  </Link>
                </div>
              </aside>
            </div>
          </div>
        </article>
      </main>

      <Footer />

      {/* Schema.org Article */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.metaDescription,
            image: article.imageUrl,
            inLanguage: 'it',
            datePublished: '2026-07-14',
            dateModified: '2026-07-14',
            author: {
              '@type': 'Organization',
              name: 'Itaobuy Spreadsheet Italia',
              url: SITE_URL,
            },
            publisher: {
              '@type': 'Organization',
              name: 'Itaobuy Spreadsheet Italia',
              url: SITE_URL,
              logo: {
                '@type': 'ImageObject',
                url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/62f99c0415358bd5388d5ed1df9ac1ff.jpeg',
              },
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `${SITE_URL}/articolo/${article.slug}`,
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: article.faqs.map((faq) => ({
              '@type': 'Question',
              name: faq.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: faq.answer,
              },
            })),
          }),
        }}
      />
    </div>
  );
}